import { useState } from "react";
import { OFFERS } from "@/data/offers";
import { Reveal, Kicker } from "./Reveal";
import { ArrowUpRight, Clock, Users, Package, GraduationCap } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const SPANS = {
  "diagnostic-agile": "md:col-span-7 md:row-span-2",
  "amelioration-processus": "md:col-span-5",
  "ingenierie-exigences": "md:col-span-5",
  "referentiels-conformite": "md:col-span-6",
  formations: "md:col-span-6",
};

const Section = ({ title, items }) => (
  <div>
    <h4 className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-teal-300 mb-3">{title}</h4>
    <ul className="space-y-2">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3 text-sm text-slate-300 leading-relaxed">
          <span className="text-teal-400 mt-0.5 shrink-0">—</span>
          {it}
        </li>
      ))}
    </ul>
  </div>
);

const OfferDialog = ({ offer, onClose }) => (
  <Dialog open={!!offer} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      data-testid="offer-dialog"
      data-lenis-prevent
      className="max-w-3xl max-h-[85vh] overflow-y-auto bg-[#0f1524] border-white/10 text-slate-100 rounded-none"
    >
      {offer && (
        <>
          <DialogHeader>
            <div className="font-mono2 text-[11px] tracking-[0.25em] uppercase grad-text">
              Offre {offer.code}
            </div>
            <DialogTitle className="font-heading text-xl sm:text-2xl font-semibold text-slate-50">
              {offer.title}
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-sm leading-relaxed">
              {offer.description}
            </DialogDescription>
          </DialogHeader>
          <div className="grid sm:grid-cols-2 gap-8 mt-4">
            {offer.objectifs && <Section title="Objectifs" items={offer.objectifs} />}
            {offer.deroulement && <Section title="Déroulement" items={offer.deroulement} />}
            {offer.livrables && <Section title="Livrables" items={offer.livrables} />}
            {offer.catalogue && (
              <div className="sm:col-span-2">
                <h4 className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-teal-300 mb-3">
                  Catalogue
                </h4>
                <div className="grid sm:grid-cols-2 gap-4">
                  {offer.catalogue.map((c) => (
                    <div key={c.domaine} className="border border-white/10 p-4">
                      <div className="text-sm font-semibold text-slate-200">{c.domaine}</div>
                      <ul className="mt-2 space-y-1">
                        {c.items.map((it) => (
                          <li key={it} className="text-xs text-slate-400">• {it}</li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {offer.formules && (
              <div className="sm:col-span-2">
                <h4 className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-teal-300 mb-3">
                  Formules & durées
                </h4>
                <div className="grid sm:grid-cols-2 gap-3">
                  {offer.formules.map((f) => (
                    <div key={f.nom} className="flex items-center justify-between border border-white/10 px-4 py-3">
                      <span className="text-sm text-slate-200">{f.nom}</span>
                      <span className="flex items-center gap-1.5 text-xs text-teal-300">
                        <Clock size={12} /> {f.duree}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className="sm:col-span-2">
              <h4 className="font-mono2 text-[11px] tracking-[0.2em] uppercase text-teal-300 mb-3">
                Pour qui ?
              </h4>
              <div className="flex flex-wrap gap-2">
                {offer.cibles.map((c) => (
                  <span key={c} className="text-xs text-slate-300 border border-white/15 px-3 py-1.5">
                    {c}
                  </span>
                ))}
              </div>
              {offer.secteurs && <p className="mt-3 text-xs text-slate-500">{offer.secteurs}</p>}
            </div>
          </div>
          <button
            data-testid="offer-dialog-contact-btn"
            onClick={() => {
              onClose();
              document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" });
            }}
            className="btn-primary mt-4 w-full sm:w-auto"
          >
            Échanger sur cette offre <ArrowUpRight size={15} />
          </button>
        </>
      )}
    </DialogContent>
  </Dialog>
);

export default function Offers() {
  const [selected, setSelected] = useState(null);

  return (
    <section id="offres" className="relative py-16 md:py-24 lg:py-32 bg-[#0d1322] noise" data-testid="offers-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal>
          <Kicker>Les offres packagées</Kicker>
          <div className="mt-5 flex flex-col md:flex-row md:items-end md:justify-between gap-6">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-slate-50 max-w-xl">
              Cinq offres calibrées, du diagnostic flash à la transformation.
            </h2>
            <p className="text-slate-500 text-sm max-w-sm">
              Chaque offre livre des artefacts concrets et un plan d'action priorisé. Cliquez pour le détail complet.
            </p>
          </div>
        </Reveal>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-5 auto-rows-fr">
          {OFFERS.map((offer, i) => {
            const isHero = offer.id === "diagnostic-agile";
            return (
              <Reveal key={offer.id} delay={i * 0.07} className={`col-span-1 ${SPANS[offer.id]}`}>
                <button
                  data-testid={`offer-card-${offer.id}`}
                  onClick={() => setSelected(offer)}
                  className="group relative w-full h-full text-left border border-white/10 bg-[#131b2c] p-7 sm:p-8 card-lift flex flex-col"
                >
                  <div className="flex items-start justify-between">
                    <span className="font-mono2 text-[11px] tracking-[0.25em] uppercase text-slate-500 group-hover:text-teal-300 transition-colors">
                      {String(i + 1).padStart(2, "0")} / {offer.code}
                    </span>
                    <ArrowUpRight
                      size={18}
                      className="text-slate-600 group-hover:text-teal-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-all"
                    />
                  </div>
                  <h3 className={`mt-6 font-heading font-semibold text-slate-50 ${isHero ? "text-2xl sm:text-3xl" : "text-xl sm:text-2xl"}`}>
                    {offer.title}
                  </h3>
                  <p className={`mt-3 text-sm text-slate-400 leading-relaxed ${isHero ? "" : "flex-1"}`}>{offer.tagline}</p>
                  {isHero && (
                    <>
                      <p className="mt-4 text-sm text-slate-500 leading-relaxed hidden md:block">{offer.description}</p>
                      <div className="mt-6 flex-1 min-h-[140px] hidden md:block overflow-hidden border border-white/10">
                        <img
                          src="https://images.unsplash.com/photo-1531403009284-440f080d1e12?crop=entropy&cs=srgb&fm=jpg&q=85&w=900"
                          alt="Diagnostic Agile"
                          className="w-full h-full object-cover opacity-60 group-hover:opacity-80 group-hover:scale-[1.02] transition-all duration-500"
                        />
                      </div>
                    </>
                  )}
                  <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-500">
                    {offer.formules && (
                      <span className="flex items-center gap-1.5">
                        <Package size={13} className="text-blue-400" /> {offer.formules.length} formules
                      </span>
                    )}
                    {offer.certifications && (
                      <span className="flex items-center gap-1.5">
                        <GraduationCap size={13} className="text-amber-400" />
                        {offer.certifications.join(" · ")}
                      </span>
                    )}
                    <span className="flex items-center gap-1.5">
                      <Users size={13} className="text-teal-400" /> {offer.cibles[0]}
                    </span>
                  </div>
                  <span className="absolute bottom-0 left-0 h-[2px] w-0 grad-line group-hover:w-full transition-all duration-500" />
                </button>
              </Reveal>
            );
          })}
        </div>
      </div>
      <OfferDialog offer={selected} onClose={() => setSelected(null)} />
    </section>
  );
}
